import { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { ContentItem } from '../types/content';

interface FeatureEntry {
  title: string;
  description: string;
}

interface AboutContentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (item: ContentItem) => void;
  item: ContentItem | null;
  sectionId: string;
}

function parseContentValue(raw: string): any {
  if (typeof raw !== 'string') return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return raw.replace(/^"|"$/g, '');
  }
}

function isFeatureArray(data: any): data is FeatureEntry[] {
  return Array.isArray(data) && data.length > 0 && data.every(
    (item: any) => typeof item === 'object' && item !== null && 'title' in item && 'description' in item
  );
}

export default function AboutContentModal({
  isOpen,
  onClose,
  onSave,
  item,
  sectionId
}: AboutContentModalProps) {
  const [formData, setFormData] = useState<ContentItem>({
    id: crypto.randomUUID(),
    title: '',
    type: 'text',
    content: '',
    status: 'Draft'
  });
  const [features, setFeatures] = useState<FeatureEntry[]>([]);
  const [useFeatureEditor, setUseFeatureEditor] = useState(false);

  useEffect(() => {
    if (item) {
      const content = parseContentValue(item.content);

      if (isFeatureArray(content)) {
        setFeatures(content.map(f => ({ title: f.title, description: f.description })));
        setUseFeatureEditor(true);
        setFormData({ ...item, content: '' });
      } else {
        setUseFeatureEditor(false);
        setFeatures([]);
        setFormData({
          ...item,
          content: typeof content === 'object' ? JSON.stringify(content, null, 2) : content
        });
      }
    } else {
      setUseFeatureEditor(false);
      setFeatures([]);
      setFormData({
        id: crypto.randomUUID(),
        title: '',
        type: 'text',
        content: '',
        status: 'Draft'
      });
    }
  }, [item, sectionId]);

  const handleTypeChange = (type: ContentItem['type']) => {
    setFormData({ ...formData, type });
    if (type === 'data' && !useFeatureEditor) {
      setUseFeatureEditor(true);
      setFeatures(features.length > 0 ? features : [{ title: '', description: '' }]);
    } else if (type !== 'data') {
      setUseFeatureEditor(false);
    }
  };

  const handleFeatureChange = (index: number, field: keyof FeatureEntry, value: string) => {
    const updated = [...features];
    updated[index] = { ...updated[index], [field]: value };
    setFeatures(updated);
  };

  const addFeature = () => {
    setFeatures([...features, { title: '', description: '' }]);
  };

  const removeFeature = (index: number) => {
    setFeatures(features.filter((_, i) => i !== index));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    let content: string;
    if (useFeatureEditor) {
      const validFeatures = features.filter(f => f.title.trim() || f.description.trim());
      content = JSON.stringify(validFeatures);
    } else {
      const raw = formData.content;
      if (typeof raw === 'string') {
        const trimmed = raw.trim();
        if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
          try {
            JSON.parse(trimmed);
            content = trimmed;
          } catch {
            content = JSON.stringify(trimmed);
          }
        } else {
          content = JSON.stringify(trimmed);
        }
      } else {
        content = raw;
      }
    }

    onSave({ ...formData, content });
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                  as="h3"
                  className="text-lg font-medium leading-6 text-gray-900 mb-4"
                >
                  {item ? 'Edit Content' : 'Add New Content'}
                </Dialog.Title>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Title
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Type
                    </label>
                    <select
                      value={formData.type}
                      onChange={(e) => handleTypeChange(e.target.value as ContentItem['type'])}
                      className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                    >
                      <option value="text">Text</option>
                      <option value="media">Media</option>
                      <option value="data">Data</option>
                    </select>
                  </div>

                  {useFeatureEditor ? (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Features
                        </label>
                        <button
                          type="button"
                          onClick={addFeature}
                          className="inline-flex items-center gap-1 text-sm text-golf-orange hover:text-golf-orange/80 transition-colors"
                        >
                          <PlusIcon className="w-4 h-4" />
                          Add Feature
                        </button>
                      </div>
                      <div className="space-y-3 max-h-80 overflow-y-auto">
                        {features.map((feature, idx) => (
                          <div key={idx} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <span className="text-xs font-medium text-gray-400 mt-1">#{idx + 1}</span>
                              <button
                                type="button"
                                onClick={() => removeFeature(idx)}
                                className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            </div>
                            <input
                              type="text"
                              placeholder="Feature title"
                              value={feature.title}
                              onChange={(e) => handleFeatureChange(idx, 'title', e.target.value)}
                              className="mb-2 block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange text-sm"
                            />
                            <textarea
                              placeholder="Feature description"
                              value={feature.description}
                              onChange={(e) => handleFeatureChange(idx, 'description', e.target.value)}
                              rows={2}
                              className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange text-sm"
                            />
                          </div>
                        ))}
                        {features.length === 0 && (
                          <div className="text-center py-4 text-sm text-gray-400">
                            No features yet. Click "Add Feature" to get started.
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Content
                      </label>
                      <textarea
                        value={formData.content}
                        onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                        rows={8}
                        className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange font-mono"
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Status
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value as 'Published' | 'Draft' })}
                      className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                    >
                      <option value="Draft">Draft</option>
                      <option value="Published">Published</option>
                    </select>
                  </div>

                  <div className="mt-6 flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 text-sm font-medium text-white bg-golf-orange rounded-lg hover:bg-golf-orange/90"
                    >
                      {item ? 'Save Changes' : 'Add Content'}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}